import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Flex, Form, List, message, Row } from 'antd';
import { CustomButton, CustomInput, CustomTitle } from '../../components';
import {
  LinkSensorsAccount,
  LinkSensorsAccountModel,
} from '../../apis/onboarding';
import { useToken } from '../../common/helper';
import { useSpin } from '../../components/SpinContext';

interface DescriptionItem {
  title: string;
  isLink: boolean;
  href?: string;
  linkText?: string;
}

interface SensorData {
  description: DescriptionItem[];
  required_inputs: string[];
}

interface ConnectsSensorsProps {
  closeModal: () => void;
  id: string;
  onSuccess: () => void;
  sensorData: SensorData;
}

interface FormValues {
  [key: string]: string;
}

/**
 * Modal component for connecting sensor accounts by providing credentials
 *
 * @param closeModal - Function to close the modal
 * @param id - Sensor ID to link
 * @param onSuccess - Callback function called on successful linking
 * @param sensorData - Sensor configuration with instructions and required inputs
 */
const ConnectsSensors: React.FC<ConnectsSensorsProps> = ({
  closeModal,
  id,
  onSuccess,
  sensorData,
}) => {
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const { token, removeToken } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const navigate = useNavigate();
  const [sensorModalInputs, setSensorModalInputs] = useState<string[]>([]);

  useEffect(() => {
    if (
      sensorData?.required_inputs &&
      Array.isArray(sensorData.required_inputs)
    ) {
      setSensorModalInputs(sensorData.required_inputs);
    } else {
      setSensorModalInputs([]);
    }
    form.resetFields();
  }, [sensorData, form]);

  const description: DescriptionItem[] = sensorData?.description || [];

  const unauthorize = (error: { status?: number }) => {
    if (error && error?.status === 401) {
      removeToken();
      navigate('/login');
    }
  };

  const handleCancel = () => {
    closeModal();
  };

  const LinkSensors = (model: LinkSensorsAccountModel) => {
    setIsFormValid(true);
    showSpin();
    LinkSensorsAccount(token, model)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            onSuccess();
            message.success(data?.data?.message);
            closeModal();
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((err) => {
        unauthorize(err);
      })
      .finally(() => {
        hideSpin();
        setIsFormValid(false);
      });
  };

  const onFinishForm = (values: FormValues) => {
    if (values && Object.keys(values).length > 0 && id) {
      const model: LinkSensorsAccountModel = {
        sensor_id: id,
        credentials: { ...values },
      };
      LinkSensors(model);
    }
  };

  const initialFormValues: Record<string, string> = sensorModalInputs.reduce(
    (acc: Record<string, string>, inputName: string) => {
      acc[inputName] = '';
      return acc;
    },
    {}
  );

  return (
    <Form
      form={form}
      name='validateOnly'
      initialValues={initialFormValues}
      layout='vertical'
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 24 }}
      autoComplete='off'
      onFinish={onFinishForm}
    >
      <Row gutter={12}>
        <Col span={24}>
          <div className='sensor-info'>
            <div style={{ paddingLeft: '6px' }}>
              <CustomTitle
                text='Instructions'
                fontSize='14px'
                fontWeight='500'
                color='black'
              />
              <List
                split={false}
                itemLayout='horizontal'
                dataSource={description}
                renderItem={(item, index) => (
                  <List.Item style={{ padding: '0' }}>
                    <List.Item.Meta
                      title={
                        item.isLink ? (
                          <>
                            <span style={{ fontWeight: 400 }}>
                              {index + 1}. {item.title}{' '}
                            </span>
                            <a
                              href={item?.href}
                              target='_blank'
                              rel='noopener noreferrer'
                              style={{
                                textDecoration: 'underline',
                                fontWeight: 400,
                              }}
                            >
                              {item.linkText}
                            </a>
                          </>
                        ) : (
                          <span style={{ fontWeight: 400 }}>
                            {index + 1}. {item?.title}
                          </span>
                        )
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
            <Col
              xxl={12}
              xl={12}
              lg={16}
              md={24}
              sm={24}
              xs={24}
              style={{ marginTop: '20px' }}
            >
              {sensorModalInputs.map((element) => (
                <Form.Item
                  key={element}
                  label={element}
                  name={element}
                  style={{ paddingTop: '12px' }}
                  /* @ts-expect-error - NOTE(tim) this doesn't typecheck, but it was in the original JSX  */
                  labelCol={{ width: '100%' }}
                  rules={[
                    {
                      required: true,
                      message: `Please input ${element}`,
                    },
                  ]}
                >
                  <CustomInput
                    placeholder={`Paste ${element} here`}
                    height='36px'
                    width='100%'
                    border='0px'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-secondary)'
                    borderRadius='8px'
                    placeholderColor='var(--text-secondary)'
                    lineHeight='38px'
                  />
                </Form.Item>
              ))}
            </Col>
          </div>
        </Col>
      </Row>
      <Flex justify='flex-end' gap={12} style={{ marginTop: 24 }}>
        <CustomButton
          key='cancel'
          onClick={handleCancel}
          text={'Cancel'}
          bgColor={'transparent'}
          color={'black'}
          height='40px'
          borderRadius='8px'
          border='1px solid #E6E6E6'
        />
        <Form.Item shouldUpdate noStyle>
          {() => (
            <CustomButton
              key='submit'
              onClick={() => form.submit()}
              text={'Link Account'}
              height='40px'
              bgColor={'#000'}
              color={'#fff'}
              borderRadius='8px'
              isFormValid={
                !form.isFieldsTouched(true) ||
                !!form.getFieldsError().filter(({ errors }) => errors.length)
                  .length ||
                isFormValid
              }
            />
          )}
        </Form.Item>
      </Flex>
    </Form>
  );
};

export default ConnectsSensors;
